/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

import Box from '@mui/material/Box/Box';
import MuiInputBase from '@mui/material/InputBase';
import styled from '@mui/material/styles/styled';
import {
  type ChangeEvent,
  memo,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
  type SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { GatherLightSystemTokens } from '@marketplace/gather-ui/theme/colors';
import { TypographyStyle } from '@marketplace/gather-ui/theme/configs';
import {
  AppSpacing,
  BorderOptions,
  BorderRadius,
  ZIndex,
} from '@marketplace/gather-ui/theme/constants';

import { useSearchDropdown } from '../dropdown/search-dropdown.provider';

import { EndAdornment } from './end-adornment.component';
import { StartAdornment } from './start-adornment.component';

const styleRelative = { position: 'relative' };

// styled input for the search field
const SearchInputField = memo(
  styled(MuiInputBase)(() => ({
    ...TypographyStyle.BodyLarge,
    '&:hover': {
      boxShadow: 'none',
    },
    '&.Mui-focused': {
      boxShadow: 'none',
    },
    '& .MuiInputBase-input': {
      padding: 0,
    },
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: BorderRadius.SM,
    boxShadow: 'none',
    color: GatherLightSystemTokens.OnSurface.primary,
    paddingBottom: AppSpacing.SM,
    paddingLeft: AppSpacing.MD,
    paddingRight: AppSpacing.MD,
    paddingTop: AppSpacing.SM,
    transition: 'box-shadow 120ms ease',
    width: '100%',
  }))
);

// input wrapper that transitions between form variant (default) and active dropdown styling
export const InputWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ isActive, theme }) => ({
  backgroundColor: theme.palette.background.paper,
  // default: form variant styling (rounded corners, border)
  // active: no shadow here - shadow comes entirely from DropdownShadowWrapper in popper
  border: isActive ? 'none' : BorderOptions.BorderDefault,
  borderRadius: isActive ? undefined : `${BorderRadius.Alert}px`,
  boxShadow: 'none',
  position: 'relative',
  transition: 'border-radius 200ms ease',
  // keep the input above the popper shadow so it never gets covered
  zIndex: ZIndex.searchInput,
  // Mobile: grey border by default, dark border when active
  [theme.breakpoints.down('md')]: {
    border: isActive ? BorderOptions.BorderInputActive : BorderOptions.BorderDefault,
    borderRadius: isActive
      ? `${BorderRadius.Alert}px ${BorderRadius.Alert}px 0 0`
      : `${BorderRadius.Alert}px`,
  },
}));

type SearchInputProps = {
  disabled: boolean;
  inputRef: Ref<unknown>;
  loading: boolean;
  onBlur: (focusEvent: ReactFocusEvent<HTMLElement>) => void;
  onChange: (changeEvent: ChangeEvent<HTMLInputElement>) => Promise<void>;
  onFocus: () => void;
  onKeyDown: (keyboardEvent: ReactKeyboardEvent<HTMLInputElement>) => void;
  onReset: () => void;
  onSubmit: (inputEvent: SyntheticEvent) => void;
  value: string;
};

export function SearchInput({
  disabled,
  inputRef,
  loading,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  onReset,
  onSubmit,
  value,
}: SearchInputProps): ReactNode {
  const ref = useRef<HTMLInputElement>(null);

  const { closeDropdown, dropdownState, openDropdown } = useSearchDropdown();

  const handleBack = useCallback(
    (inputEvent: SyntheticEvent) => {
      inputEvent.stopPropagation();
      closeDropdown();
    },
    [closeDropdown]
  );

  const handleReset = useCallback(
    (inputEvent: SyntheticEvent) => {
      inputEvent.stopPropagation();

      /**
       * MANUALLY TRIGGER BLUR BEFORE RESET:
       * Similar to the Enter key handler, when filters (like zipcode) have been applied
       * and the user clicks the clear button, the state updates from onReset can cause
       * the input to become disabled or enter a transitional state. If the input still
       * has focus when this happens, React/Formik can crash trying to process a blur
       * event on a disabled component (error: "reading 'persist'"). By blurring manually
       * here, we ensure the blur event finishes cleanly BEFORE the reset/state changes.
       */
      if (ref.current) {
        ref.current.blur();
      }

      onReset();

      // Return focus to the input after state has settled
      setTimeout(() => {
        ref.current?.focus();
      }, 0);
    },
    [onReset]
  );

  const endAdornment = useMemo(() => {
    return (
      <EndAdornment
        inputContainsValue={value?.length > 0}
        onReset={handleReset}
        onSubmit={onSubmit}
      />
    );
  }, [handleReset, onSubmit, value?.length]);

  const startAdornment = useMemo(() => {
    return <StartAdornment onBack={handleBack} />;
  }, [handleBack]);

  return (
    <InputWrapper isActive={dropdownState !== 'closed'} onClick={openDropdown}>
      <form autoComplete='off' onSubmit={onSubmit}>
        <SearchInputField
          aria-controls={dropdownState !== 'closed' ? 'search-suggestions-dropdown' : undefined}
          aria-expanded={dropdownState !== 'closed'}
          aria-haspopup='listbox'
          aria-label='Search vehicles'
          disabled={loading || disabled}
          endAdornment={endAdornment}
          id='search'
          inputRef={ref}
          name='search'
          placeholder={dropdownState !== 'opened' ? 'Search by keyword' : undefined}
          ref={inputRef}
          startAdornment={startAdornment}
          sx={styleRelative}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
        />
      </form>
    </InputWrapper>
  );
}
